import { createRoot } from 'react-dom/client';

import { IPicsartToolPubSub } from '@picsart/miniapps-sdk/types/types';
import setupContext from './context';
import prefetchPresetFeedData from '@/utils/query/prefetchPresetFeedData';
import Wrappers from './Wrappers';
import App from './App';

type Theme = 'dark' | 'light';

/**
 * Pin <html> to a single theme class and keep it pinned.
 *
 * The design-system tokens live under `:root.dark` / `:root.light`, so the
 * class on the root element drives every token color. We can't just set it
 * once: the embedded host (picsart.com) forces its own theme onto the iframe
 * two ways — (1) a `?theme=` URL param, and (2) `@picsart/design-system`'s
 * `applyThemeClasses`, which ADDS `light` to <html> (without removing our
 * `dark`; because `:root.light` is declared after `:root.dark`, light then
 * wins the cascade). The DS re-adds it on mount and on every host theme change.
 *
 * So we ignore the host entirely and keep <html> pinned to the forced theme via
 * a MutationObserver that strips the unwanted class whenever it reappears.
 * Returns a cleanup that disconnects the observer.
 *
 * `?forceTheme=light` (a distinct param the host does NOT set) is the one
 * escape hatch — used to diagnose UI still hardcoding colors instead of DS
 * tokens.
 */
const pinTheme = (theme: Theme): (() => void) => {
  const other: Theme = theme === 'dark' ? 'light' : 'dark';
  const html = document.documentElement;
  const enforce = () => {
    // Guard each mutation so the observer settles instead of looping on its
    // own class changes.
    if (html.classList.contains(other)) html.classList.remove(other);
    if (!html.classList.contains(theme)) html.classList.add(theme);
  };
  enforce();
  const observer = new MutationObserver(enforce);
  observer.observe(html, { attributes: true, attributeFilter: ['class'] });
  return () => observer.disconnect();
};

const setup = ({
  containerId,
  setupTheming = true,
  pubsub,
}: {
  containerId: string;
  setupTheming?: boolean;
  pubsub?: IPicsartToolPubSub;
}) => {
  const container = document.getElementById(containerId);
  if (!container) {
    throw new Error(
      `[GenAI Studio] container element #${containerId} not found`
    );
  }

  // Force dark in prod AND dev — a light host can never leak through.
  const forcedTheme: Theme =
    new URLSearchParams(window.location.search).get('forceTheme') === 'light'
      ? 'light'
      : 'dark';
  const unpinTheme = pinTheme(forcedTheme);

  const root = createRoot(container);
  setupContext({ pubsub });
  // Context just became available — start the library data request now,
  // before the first render, so screens mount with it already in flight.
  prefetchPresetFeedData();

  root.render(
    <Wrappers setupTheming={setupTheming}>
      <App />
    </Wrappers>
  );

  return {
    unmount: () => {
      root.unmount();
      unpinTheme();
    },
  };
};

export default setup;
